/* ─────────────────────────────────────────────
   IDENTITY BACKUP (zero-knowledge key export / restore) — ADR 0016
   Export encrypts a claimed device's identity (raw ed25519 seed + label +
   group) with a passphrase via shared/backup.ts and POSTs the ciphertext to
   the owner's account; restore lists the account's backups, decrypts a chosen
   one locally, and reinstalls the keypair. The plaintext seed + passphrase
   never leave the browser — the server stores ciphertext only.
   ───────────────────────────────────────────── */
import { $, qsa, escapeHtml } from './dom';
import { showToast } from './toast';
import { deviceDisplayName } from './animals';
import { getAuthUserId, getMyClaims } from '../state/auth-state';
import { MOCK } from '../state/device';
import {
  loadKeypair, saveKeypair, rememberDevice,
} from '../storage/persistence';
import {
  apiListBackups,
  apiPutBackup,
  apiGetBackup,
  apiDeleteBackup,
} from '../api/backup';
import {
  encryptBackup,
  decryptBackup,
  pkcs8ToRawSeed,
  rawSeedToPkcs8,
  publicKeyFromSeed,
} from '../../shared/backup';
import { b64urlEncode, b64urlDecode } from '../../shared/crypto';

/* ─────────────────────────────────────────────
   Callbacks injected by main.ts boot() to avoid circular imports
   ───────────────────────────────────────────── */
let _closeDrawerCb: (() => void) | null = null;
export function setBackupCloseDrawerCb(fn: () => void): void { _closeDrawerCb = fn; }
let _decodeTokenCb: ((token: string | null) => any) | null = null;
export function setBackupDecodeTokenCb(fn: (token: string | null) => any): void { _decodeTokenCb = fn; }
let _activateDeviceCb: ((device: any, restored: boolean) => void) | null = null;
export function setBackupActivateDeviceCb(fn: (device: any, restored: boolean) => void): void { _activateDeviceCb = fn; }

const backupModal = $('backup-modal');
interface BackupMeta { ref: string; label_hint: string | null; updated_at: number }
let backupList: BackupMeta[] = [];
let backupSelectedRef: string | null = null;

function setBackupStatus(pane: 'export' | 'restore', msg: string, kind?: 'err' | 'ok'){
  const el = $(pane === 'export' ? 'backup-export-status' : 'backup-restore-status');
  if(!el) return;
  el.textContent = msg;
  el.className = 'backup-status' + (kind ? ' ' + kind : '');
}

// A coarse strength signal — length + character-class variety. It is guidance,
// not a gate (the only hard rule is a >=8 char minimum at export time).
function passStrength(p: string): { score: number; label: string }{
  let s = 0;
  if(p.length >= 8) s++;
  if(p.length >= 14) s++;
  if(/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if(/\d/.test(p)) s++;
  if(/[^A-Za-z0-9]/.test(p)) s++;
  const labels = ['too short', 'weak', 'fair', 'good', 'strong', 'very strong'];
  return { score: s, label: labels[Math.min(s, labels.length - 1)] };
}
function updatePassMeter(){
  const p = ($('backup-pass') as HTMLInputElement)?.value || '';
  const { score, label } = passStrength(p);
  const bar = $('backup-meter-bar');
  if(bar) bar.style.width = Math.min(100, (score / 5) * 100) + '%';
  const lbl = $('backup-meter-label');
  if(lbl) lbl.textContent = p ? label : ' ';
}

export function openBackupExport(){
  if(!MOCK.device){ showToast('no active device'); return; }
  const token = MOCK.device.token;
  if(getAuthUserId() == null){ showToast('sign in (Account) to back up'); return; }
  if(!getMyClaims()?.get(token)){ showToast('claim this device first'); return; }
  if(!loadKeypair(token)){ showToast('no signing key for this device here'); return; }
  _closeDrawerCb?.();
  $('backup-title').textContent = 'Back up identity';
  $('backup-export-pane').hidden = false;
  $('backup-restore-pane').hidden = true;
  ($('backup-pass') as HTMLInputElement).value = '';
  ($('backup-pass2') as HTMLInputElement).value = '';
  ($('backup-hint') as HTMLInputElement).value = '';
  setBackupStatus('export', '');
  updatePassMeter();
  backupModal.classList.add('is-open');
  setTimeout(() => $('backup-pass')?.focus(), 60);
}

export async function openBackupRestore(){
  if(getAuthUserId() == null){ showToast('sign in to restore'); return; }
  _closeDrawerCb?.();
  $('backup-title').textContent = 'Restore identity';
  $('backup-export-pane').hidden = true;
  $('backup-restore-pane').hidden = false;
  $('backup-restore-pass-field').hidden = true;
  $('backup-restore-go').hidden = true;
  backupSelectedRef = null;
  setBackupStatus('restore', '');
  $('backup-list').innerHTML = `<div class="acct-hint">loading…</div>`;
  backupModal.classList.add('is-open');
  try {
    const json: any = await apiListBackups();
    backupList = Array.isArray(json.backups)
      ? json.backups
          .filter((b: any) => b && typeof b.ref === 'string')
          .map((b: any) => ({ ref: b.ref, label_hint: typeof b.label_hint === 'string' ? b.label_hint : null, updated_at: b.updated_at || 0 }))
      : [];
    renderBackupList();
  } catch (err) {
    console.warn('[locus] list backups failed', err);
    $('backup-list').innerHTML = `<div class="acct-hint">could not load backups</div>`;
  }
}

function renderBackupList(){
  const box = $('backup-list');
  if(!box) return;
  if(backupList.length === 0){ box.innerHTML = `<div class="acct-hint">No backups on this account yet.</div>`; return; }
  box.innerHTML = backupList.map((b) => {
    const dec = _decodeTokenCb?.(b.ref);
    // deviceDisplayName resolves animal names AND routes a high-entropy token to
    // the "Private · <tail>" form — important now that decodeToken returns raw
    // 26-char segments for private tokens (it returns escaped HTML either way).
    const fallback = dec
      ? deviceDisplayName(b.ref, dec.animals, dec.group)
      : escapeHtml(b.ref.split('-').slice(-1)[0]);
    const name = b.label_hint ? escapeHtml(b.label_hint) : fallback;
    const tail = escapeHtml(b.ref.split('-').slice(-1)[0]);
    return `<div class="backup-row ${b.ref === backupSelectedRef ? 'is-sel' : ''}" data-ref="${escapeHtml(b.ref)}">` +
      `<span class="br-name">${name}</span><span class="br-meta">${tail}</span>` +
      `<button class="br-del danger" data-ref="${escapeHtml(b.ref)}" aria-label="Delete backup">Delete</button></div>`;
  }).join('');
  qsa('.backup-row', box).forEach((r: any) => r.addEventListener('click', () => selectBackup(r.dataset.ref)));
  // Delete is isolated from the row's select-click (stopPropagation) so tapping
  // it never opens the passphrase pane for that row.
  qsa('.br-del', box).forEach((b: any) => b.addEventListener('click', (e: any) => {
    e.stopPropagation();
    deleteBackup(b.dataset.ref);
  }));
}

// Delete a backup blob from the account. Reaches the relaxed DELETE gate
// (backup-ownership, not a live claim), so an orphaned backup whose device claim
// was revoked is removable. Refreshes the list and clears the selection if the
// deleted row was selected.
async function deleteBackup(ref: string){
  try {
    await apiDeleteBackup(ref);
  } catch (err) {
    console.warn('[locus] delete backup failed', err);
    showToast('could not delete backup');
    return;
  }
  backupList = backupList.filter((b) => b.ref !== ref);
  if(backupSelectedRef === ref){
    backupSelectedRef = null;
    $('backup-restore-pass-field').hidden = true;
    $('backup-restore-go').hidden = true;
  }
  renderBackupList();
  showToast('backup deleted');
}

function selectBackup(ref: string){
  backupSelectedRef = ref;
  renderBackupList();
  $('backup-restore-pass-field').hidden = false;
  $('backup-restore-ref').textContent = ref.split('-').slice(-1)[0];
  ($('backup-restore-pass') as HTMLInputElement).value = '';
  $('backup-restore-go').hidden = false;
  setBackupStatus('restore', '');
  setTimeout(() => $('backup-restore-pass')?.focus(), 30);
}

async function doExport(){
  if(!MOCK.device) return;
  const token = MOCK.device.token;
  const pass = ($('backup-pass') as HTMLInputElement).value;
  const pass2 = ($('backup-pass2') as HTMLInputElement).value;
  if(pass.length < 8){ setBackupStatus('export', 'use a passphrase of at least 8 characters', 'err'); return; }
  if(pass !== pass2){ setBackupStatus('export', 'passphrases do not match', 'err'); return; }
  const kp = loadKeypair(token);
  if(!kp){ setBackupStatus('export', 'no signing key for this device', 'err'); return; }
  const hint = ($('backup-hint') as HTMLInputElement).value.trim();
  const btn = $('backup-export-go') as HTMLButtonElement;
  btn.disabled = true;
  setBackupStatus('export', 'encrypting…');
  try {
    const seed = pkcs8ToRawSeed(b64urlDecode(kp.priv));
    const label: string[] = MOCK.device.animals;
    const group: string[] = (MOCK.device.group && MOCK.device.group.length === 2) ? MOCK.device.group : [];
    const env = await encryptBackup(pass, { label, group, seed }, { ref: token, label_hint: hint || null });
    const res = await apiPutBackup(env);
    if(res.status === 404){ setBackupStatus('export', 'claim this device first', 'err'); return; }
    if(res.status === 401){ setBackupStatus('export', 'please sign in again', 'err'); return; }
    if(!res.ok) throw new Error('backup ' + res.status);
    setBackupStatus('export', 'backed up — sign in on another device to restore it', 'ok');
    showToast('identity backed up');
  } catch (err) {
    console.warn('[locus] backup failed', err);
    setBackupStatus('export', 'backup failed', 'err');
  } finally {
    btn.disabled = false;
  }
}

async function doRestore(){
  if(!backupSelectedRef) return;
  const ref = backupSelectedRef;
  const pass = ($('backup-restore-pass') as HTMLInputElement).value;
  if(!pass){ setBackupStatus('restore', 'enter the passphrase', 'err'); return; }
  const btn = $('backup-restore-go') as HTMLButtonElement;
  btn.disabled = true;
  setBackupStatus('restore', 'fetching…');
  try {
    let env: any;
    try { env = await apiGetBackup(ref); }
    catch (e: any) {
      if(e?.status === 404){ setBackupStatus('restore', 'backup not found (claim revoked?)', 'err'); return; }
      throw e;
    }
    setBackupStatus('restore', 'decrypting…');
    let restored;
    try { restored = await decryptBackup(pass, env); }
    catch { setBackupStatus('restore', 'wrong passphrase or corrupt backup', 'err'); return; }
    // Reinstall the keypair: rebuild the pkcs8 private key from the raw seed and
    // recover the raw public key from it (the store wants {priv, pub}). Use the
    // server token `ref` as the canonical token (robust to a create-time
    // attempt-salt that tokenFor(label,group) wouldn't reproduce).
    const priv = b64urlEncode(rawSeedToPkcs8(restored.seed));
    const pub = b64urlEncode(await publicKeyFromSeed(restored.seed));
    saveKeypair(ref, { priv, pub });
    const entry = { token: ref, animals: restored.label, group: restored.group, created_at: restored.created_at || Date.now() };
    rememberDevice(entry);
    setBackupStatus('restore', 'restored', 'ok');
    showToast('identity restored');
    backupModal.classList.remove('is-open');
    _activateDeviceCb?.(entry, /*restored*/ true);
  } catch (err) {
    console.warn('[locus] restore failed', err);
    setBackupStatus('restore', 'restore failed', 'err');
  } finally {
    btn.disabled = false;
  }
}

export function initBackupModal(){
  backupModal?.addEventListener('click', (e: any) => { if(e.target === backupModal) backupModal.classList.remove('is-open'); });
  $('backup-close')?.addEventListener('click', () => backupModal.classList.remove('is-open'));
  $('backup-pass')?.addEventListener('input', updatePassMeter);
  $('backup-export-go')?.addEventListener('click', doExport);
  $('backup-restore-go')?.addEventListener('click', doRestore);
}
